// MACHINERY V2 (Stroje) — SR create/edit: the PRÍLOHY field (M2.6).
//
// Spec: data package §7 (`files`) + §8 ("Files edit nuance"). Reference impl:
// components/CreateServiceReportPage.tsx:860-956 (dropzone, hidden input, the
// chip list) and design_handoff_machinery_v3/machinery-sr.jsx `DropZone`.
// Artboards 0702 / 0703.
//
// Split out of SrCreateFields.tsx for the 300-line cap.
//
// PER-SURFACE CAP: 10 MB (useSrCreateForm.MAX_FILE_SIZE_MB). The Dokumenty tab
// is 11 MB and the two are deliberately NOT unified (ticket §5 #6). An oversize
// file is DROPPED with one message, exactly as v1 does — it is not queued, not
// retried, and the rest of the drop still lands.
//
// >>> SOLVING DOCUMENTS (typeCode 2) ARE LISTED HERE AND THEY MUST BE. <<<
// On edit the list is `serviceReport.files` in full, so a solved report shows
// its solving documents as removable chips — and removing one is a SERVER
// NO-OP (the reconciliation is typeCode-1-only; the verified server citation is
// in useSrCreateForm.ts, preserved behaviour 2). This is v1 behaviour that the
// data package explicitly says to reproduce, not a defect introduced here. Do
// not filter the list and do not disable those chips: either would change what
// the user sees from what v1 shows, without changing what the server does.
//
// The FILE TILE and the SIZE FORMATTER are the M2.5 implementations, imported
// (`FileTile`, `formatDocSize`) — one file-chip idiom for the whole section.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { ServiceReportFile } from "../../models/ServiceReportsModel";
import FormDropzone from "../forms/FormDropzone";
import { FormError } from "../forms/FormField";
import { FileTile } from "../profile/tabs/docsRows";
import { formatDocSize } from "../profile/tabs/tabFormat";

export interface SrAttachmentsFieldProps {
  files: ServiceReportFile[];
  fileError?: string;
  onAdd: (files: File[]) => void;
  onRemove: (index: number) => void;
}

const SrAttachmentsField: React.FC<SrAttachmentsFieldProps> = ({
  files,
  fileError,
  onAdd,
  onRemove,
}) => {
  const { translate } = useTranslation("myMachinery");

  return (
    <>
      <FormDropzone
        hint={translate("myMachinery:dropFilesHere")}
        limit={translate("myMachinery:supportedFormatsHint")}
        onFiles={onAdd}
        invalid={!!fileError}
      />

      <FormError message={fileError} />

      {files.length > 0 && (
        <div
          style={{
            marginTop: 10,
            display: "flex",
            flexDirection: "column",
            gap: 8,
            minWidth: 0,
          }}
        >
          {files.map((file, index) => (
            <div
              key={file.name + "-" + index}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "8px 10px",
                borderRadius: 8,
                background: "var(--card)",
                border: "1px solid var(--border)",
                minWidth: 0,
              }}
            >
              <FileTile fileName={file.name} />
              <span
                style={{
                  flex: 1,
                  minWidth: 0,
                  fontSize: 13,
                  fontWeight: 500,
                  color: "var(--ink)",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={file.name}
              >
                {file.name}
              </span>
              {/* v1 gates the size on `> 0` (CreateServiceReportPage.tsx:935) —
                  a zero-size chip reads as a broken upload, not as "0 B". */}
              {file.size > 0 && (
                <span
                  style={{
                    fontFamily: "var(--font-mono)",
                    fontSize: 10.5,
                    color: "var(--faint)",
                    flexShrink: 0,
                  }}
                >
                  {formatDocSize(file.size)}
                </span>
              )}
              <button
                type="button"
                onClick={() => onRemove(index)}
                aria-label={translate("common:buttons.delete")}
                style={{
                  border: 0,
                  background: "transparent",
                  color: "var(--muted)",
                  cursor: "pointer",
                  fontSize: 15,
                  lineHeight: 1,
                  padding: 2,
                  flexShrink: 0,
                }}
              >
                &times;
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
};

export default SrAttachmentsField;
